'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Plus, Trash2, ToggleLeft, ToggleRight, UserCheck, UserX } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Technician {
  id: string;
  name: string;
  employeeNumber: string;
  contactNumber: string;
  active: boolean;
  createdAt: string;
}

const BRANCHES = [
  'Bloemfontein', 'Aliwal North', 'Bethlehem', 'Welkom', 'Klerksdorp',
  'Vryburg', 'Kuruman', 'Upington', 'Springbok', 'De Aar', 'Kimberly',
];

const INITIAL_TECHNICIANS: Technician[] = [];

interface FormErrors {
  name?: string;
  employeeNumber?: string;
  contactNumber?: string;
}

export default function TechniciansPage() {
  const [technicians, setTechnicians] = useState<Technician[]>(INITIAL_TECHNICIANS);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [active, setActive] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!name.trim()) newErrors.name = 'Technician name is required';
    if (!employeeNumber.trim()) newErrors.employeeNumber = 'Employee number is required';
    if (!contactNumber.trim()) newErrors.contactNumber = 'Contact number is required';
    else if (!/^[\d\s\+\-\(\)]{7,15}$/.test(contactNumber.trim()))
      newErrors.contactNumber = 'Enter a valid contact number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const newTech: Technician = {
      id: `tech-${Date.now()}`,
      name: name.trim(),
      employeeNumber: employeeNumber.trim().toUpperCase(),
      contactNumber: contactNumber.trim(),
      active,
      createdAt: new Date().toISOString().split('T')[0],
    };
    setTechnicians((prev) => [newTech, ...prev]);
    setName('');
    setEmployeeNumber('');
    setContactNumber('');
    setActive(true);
    setErrors({});
    setShowForm(false);
  };

  const handleToggleActive = (id: string) => {
    setTechnicians((prev) =>
      prev.map((t) => (t.id === id ? { ...t, active: !t.active } : t))
    );
  };

  const handleDelete = (id: string) => {
    setTechnicians((prev) => prev.filter((t) => t.id !== id));
  };

  const filtered = technicians.filter((t) => {
    if (filterActive === 'active') return t.active;
    if (filterActive === 'inactive') return !t.active;
    return true;
  });

  const activeCount = technicians.filter((t) => t.active).length;
  const inactiveCount = technicians.filter((t) => !t.active).length;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Technicians</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage workshop technicians and their active status
            </p>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} />
            Add Technician
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total', value: technicians.length, icon: Users, color: 'text-primary' },
            { label: 'Active', value: activeCount, icon: UserCheck, color: 'text-emerald-400' },
            { label: 'Inactive', value: inactiveCount, icon: UserX, color: 'text-muted-foreground' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card-surface border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={color} />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Add Form */}
        {showForm && (
          <div className="card-surface border border-border rounded-xl p-5">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Plus size={16} className="text-primary" />
              New Technician
            </h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-field">Technician Name <span className="text-destructive">*</span></label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-field"
                    placeholder="e.g. John Smith"
                  />
                  {errors.name && <p className="field-error">{errors.name}</p>}
                </div>
                <div>
                  <label className="label-field">Employee Number <span className="text-destructive">*</span></label>
                  <input
                    type="text"
                    value={employeeNumber}
                    onChange={(e) => setEmployeeNumber(e.target.value)}
                    className="input-field font-mono-data"
                    placeholder="e.g. EMP-001"
                  />
                  {errors.employeeNumber && <p className="field-error">{errors.employeeNumber}</p>}
                </div>
                <div>
                  <label className="label-field">Contact Number <span className="text-destructive">*</span></label>
                  <input
                    type="tel"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    className="input-field"
                    placeholder="e.g. 082 555 1234"
                  />
                  {errors.contactNumber && <p className="field-error">{errors.contactNumber}</p>}
                </div>
                <div>
                  <label className="label-field">Active Status</label>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setActive((v) => !v)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        active
                          ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400' :'bg-secondary border-border text-muted-foreground'
                      }`}
                    >
                      {active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      {active ? 'Active' : 'Inactive'}
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {active ? 'Technician will appear in job card assignments' : 'Technician will be hidden from assignments'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Plus size={14} />
                  Add Technician
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setErrors({}); }}
                  className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-1 p-1 bg-secondary rounded-lg w-fit">
          {(['all', 'active', 'inactive'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilterActive(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors capitalize ${
                filterActive === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Technicians table */}
        {filtered.length === 0 ? (
          <div className="card-surface border border-border rounded-xl p-12 text-center">
            <Users size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground text-sm">
              {technicians.length === 0 ? 'No technicians added yet. Click "Add Technician" to get started.' : 'No technicians match the current filter.'}
            </p>
          </div>
        ) : (
          <div className="card-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Employee No.</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Added</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((tech) => (
                    <tr key={tech.id} className="hover:bg-secondary/20 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-foreground">{tech.name}</td>
                      <td className="px-5 py-3.5 font-mono-data text-muted-foreground">{tech.employeeNumber}</td>
                      <td className="px-5 py-3.5 text-muted-foreground">{tech.contactNumber}</td>
                      <td className="px-5 py-3.5 text-muted-foreground text-xs">{tech.createdAt}</td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => handleToggleActive(tech.id)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                            tech.active
                              ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/20' :'bg-secondary border-border text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tech.active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                          {tech.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={() => handleDelete(tech.id)}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          title="Remove technician"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
